import {
  Component,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
  TemplateRef,
} from '@angular/core';
import { Product } from './products.model';
import { ProductsService } from './products.service';
import { currentEanOf } from '../../core/utils/current-ean.util';
import { hasSellingPrice } from '../../core/utils/price-status.util';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { map } from 'rxjs/operators';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../auth/auth.service';
import { PermissionService } from '../../core/services/permission.service';

import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-products',
  standalone: true,
  templateUrl: './products.component.html',
  styleUrl: './products.component.scss',
  imports: [FormsModule, ReactiveFormsModule, RouterLink, CommonModule],
})
export class ProductsComponent implements OnInit {
  products: any[] = [];
  searchTerm = '';
  searchName = '';
  searchType: 'sku' | 'name' = 'sku';
  currentPage = 1;
  pageSize = 10;
  totalProducts = 0;
  selectedProduct: any = null;
  selectedTemplateId: string | null = null;
  printQuantity: number = 1;
  userRole: string | null = null;
  uploadUrl = `${environment.apiUrl}/products/bulk-upload`;

  private productService = inject(ProductsService);
  private modalService = inject(NgbModal);
  private toastr = inject(ToastrService);
  private authService = inject(AuthService);
  private permissionService = inject(PermissionService);

  @ViewChild('addProductModal') addProductModalRef!: ElementRef;
  @ViewChild('barcodePrintModalContent')
  barcodePrintModalRef!: TemplateRef<any>;
  productToDelete: Product | null = null;
  deleteErrorMessage: string | null = null;
  pageCount: any;
  productsLoading = false;
  productInput$ = new Subject<string>();
  hasMoreProducts: boolean = false;
  barcodeTemplates: any[] = [];

  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('bulkUploadErrorModal') bulkUploadErrorModalRef!: TemplateRef<any>;
  @ViewChild('bulkUploadPreviewModal')
  bulkUploadPreviewModalRef!: TemplateRef<any>;
  uploadType: 'CREATE' | 'UPDATE' | 'CATEGORY_CREATE' = 'CREATE';
  bulkUploadErrors: any[] = [];
  // Product-creation preview flow: file is held here between the dry-run
  // (preview endpoint) and the user's confirmation.
  uploadPreview: any = null;
  pendingUploadFile: File | null = null;
  uploadInProgress = false;

  ngOnInit() {
    this.userRole = this.authService.getRole();
    this.loadProducts();
    this.setupProductSearch();
  }

  // Check if user has view-only access (cannot edit products).
  // RAC page permission (VIEW vs FULL) wins when loaded; the hardcoded role
  // list is the legacy fallback.
  isViewOnlyAccess(): boolean {
    if (this.permissionService.hasPermissionData()) {
      return !this.permissionService.canEdit('products.list');
    }
    const restrictedRoles = [
      'AREA_MANAGER',
      'STORE_MANAGER',
      'ASM',
      'LP_TEAM',
      'GRN_TEAM',
    ];
    return restrictedRoles.includes(this.userRole || '');
  }

  // Check if user can add products (view-only users cannot add products)
  /** Primary barcode of the first variant (falls back to the pricing-history EAN). */
  primaryEan(product: any): string {
    return currentEanOf(product?.variants?.[0]);
  }

  /**
   * "price pending": the SKU has a barcode-only life so far (no row with a
   * selling price). Only judged when the payload carries pricing at all —
   * store-side users never receive prices, and must not see a false badge.
   */
  isPricePending(product: any): boolean {
    const variant = product?.variants?.[0];
    if (!variant || !Array.isArray(variant.pricing)) return false;
    return !hasSellingPrice(variant);
  }

  canAddProduct(): boolean {
    if (this.permissionService.hasPermissionData()) {
      return this.permissionService.canEdit('products.list');
    }
    const restrictedRoles = [
      'AREA_MANAGER',
      'GRN_TEAM',
      'LP_TEAM',
      'STORE_MANAGER',
      'ASM',
    ];
    return !restrictedRoles.includes(this.userRole || '');
  }

  pageSizeChange() {
    this.currentPage = 1;
    this.loadProducts();
  }

  /**
   * The list, the name search and the SKU search are ONE paginated query:
   * whichever box has text becomes the filter, and page / limit always go
   * with it. (The old per-box search called /products/search without page
   * or limit, got the backend's default 10 rows, and derived pageCount from
   * that — so a search never had a second page, and paging fell back to the
   * unfiltered list.)
   */
  private currentSearchParams() {
    const name = (this.searchName || '').trim();
    const sku = (this.searchTerm || '').trim();
    return {
      page: this.currentPage,
      limit: this.pageSize,
      context: 'PRODUCT_LIST',
      ...(name ? { query: name } : {}),
      ...(sku ? { sku } : {}),
    };
  }

  loadProducts() {
    this.productsLoading = true;
    this.productService.searchProducts(this.currentSearchParams()).subscribe({
      next: (response: any) => this.applyPage(response),
      error: () => {
        this.productsLoading = false;
        this.toastr.error('Failed to fetch products');
      },
    });
  }

  private applyPage(response: any) {
    this.products = response?.data ?? [];
    this.totalProducts = response?.total ?? 0;
    this.pageCount =
      response?.pageCount ?? (Math.ceil(this.totalProducts / this.pageSize) || 1);
    this.hasMoreProducts = this.currentPage < this.pageCount;
    this.productsLoading = false;
  }

  /** Typing in either search box → debounced, page 1. */
  onSearch() {
    this.productInput$.next(`sku:${this.searchTerm}|name:${this.searchName}`);
  }

  onSearchByName() {
    this.onSearch();
  }

  setupProductSearch() {
    this.productInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => {
          this.currentPage = 1;
          this.productsLoading = true;
        }),
        switchMap(() =>
          this.productService
            .searchProducts(this.currentSearchParams())
            .pipe(catchError(() => of({ data: [], total: 0, pageCount: 1 }))),
        ),
      )
      .subscribe((response: any) => this.applyPage(response));
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.pageCount || page === this.currentPage) return;
    this.currentPage = page;
    this.loadProducts(); // load the data for this page
  }

  // Open the modal for confirmation
  openDeleteConfirmationModal(content: any, product: Product) {
    this.productToDelete = product;
    this.modalService.open(content, {
      ariaLabelledBy: 'confirmDeleteModalLabel',
    });
  }

  // Close the modal manually
  closeModal() {
    this.modalService.dismissAll();
  }

  confirmDelete() {
    if (this.productToDelete !== null) {
      this.productService.deleteProduct(this.productToDelete.id!).subscribe({
        next: () => {
          this.toastr.success('Product deleted successfully');
          this.loadProducts();
          this.closeModal();
        },
        error: (err) => {
          this.toastr.error('Product deleting failed!');
        },
      });
    }
  }

  getVisiblePages(): number[] {
    const pages: number[] = [];
    const start = Math.max(2, this.currentPage - 2);
    const end = Math.min(this.pageCount - 1, this.currentPage + 2);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }
  // Example service call is optional – adjust to your actual service name
  toggleProductStatus(product: any): void {
    // Flip the status locally for instant UI feedback
    product.isActive = !product.isActive;

    // Optional: Persist to backend (if you have a ProductService)
    this.productService
      .updateProductStatus(product.id, product.isActive)
      .subscribe({
        next: () => console.log(`Status updated for ${product.id}`),
        error: (err) => {
          console.error('Failed to update status', err);
          // Roll back UI if API fails
          product.isActive = !product.isActive;
        },
      });
  }

  // ProductsComponent.ts

  // ... (imports and properties including @ViewChild('barcodePrintModalContent')) ...

  openBarcodePrintModal(product: any) {
    this.selectedProduct = product;
    this.selectedTemplateId = null;
    this.printQuantity = 1;
    this.barcodeTemplates = []; // Clear old data

    this.productService.getBarCodeLabelTemplates().subscribe({
      next: (data: any) => {
        const templates = data.data || data;
        this.barcodeTemplates = templates;

        if (this.barcodeTemplates.length > 0) {
          this.selectedTemplateId = this.barcodeTemplates[0].id;
        }

        // THIS IS THE CRITICAL LINE THAT OPENS THE MODAL
        this.modalService.open(this.barcodePrintModalRef, {
          ariaLabelledBy: 'barcodePrintModalLabel',
          size: 'md',
        });
      },
      error: (err: any) => {
        this.toastr.error('Failed to load barcode templates.');
        this.barcodeTemplates = [];
      },
    });
  }

  printBarcode(modal: any) {
    if (
      !this.selectedProduct ||
      !this.selectedTemplateId ||
      this.printQuantity < 1
    ) {
      this.toastr.error(
        'Please select a template and specify a valid quantity (must be 1 or more).',
      );
      return;
    }

    // 1. Find the selected template to get necessary data (like clientId)
    // Assuming barcodeTemplates is an array of objects that includes 'id' and potentially 'clientId'.
    const selectedTemplate = this.barcodeTemplates.find(
      (t: any) => t.id === this.selectedTemplateId,
    );

    // Fallback/Default Client ID if not found on the template object
    const clientId =
      selectedTemplate?.clientId || '11111111-2222-3333-4444-555555555555';
    console.log(`selected product: ${JSON.stringify(this.selectedProduct)}`);

    // 2. Construct the API payload
    const payload = {
      templateId: this.selectedTemplateId,
      productId: this.selectedProduct.id,
      quantity: this.printQuantity,
      // Vars need to be dynamically populated, using placeholders/fallbacks here:
    };

    console.log('Sending Barcode Print Payload:', payload);

    // 3. Call the service method
    this.productService.printBarCode(payload).subscribe({
      next: () => {
        this.toastr.success(
          `Successfully sent ${this.printQuantity} labels for printing.`,
        );
        modal.close(); // Close modal upon success
        this.printQuantity = 1; // Reset quantity for next print
      },
      error: (err: any) => {
        console.error('Barcode print failed:', err);
        this.toastr.error(
          'Failed to submit print job. Please check the console for details.',
        );
      },
    });
  }

  // --- Bulk Operation Logic ---

  // Download product creation template (No SKU)
  downloadCreationTemplate(): void {
    this.productService.downloadTemplate().subscribe({
      next: (blob: Blob) => {
        this.triggerDownload(blob, 'product-creation-template.xlsx');
      },
      error: (error: any) => {
        console.error('Error downloading creation template:', error);
        this.toastr.error('Failed to download creation template.');
      },
    });
  }

  // Download product update template (With SKU)
  downloadUpdateTemplate(): void {
    this.productService.downloadUpdateTemplate().subscribe({
      next: (blob: Blob) => {
        this.triggerDownload(blob, 'product-update-template.xlsx');
      },
      error: (error: any) => {
        console.error('Error downloading update template:', error);
        this.toastr.error('Failed to download update template.');
      },
    });
  }

  // ADDED: Download category creation template
  downloadCategoryTemplate(): void {
    this.productService.downloadCategoryTemplate().subscribe({
      next: (blob: Blob) => {
        this.triggerDownload(blob, 'category-creation-template.xlsx');
      },
      error: (error: any) => {
        console.error('Error downloading category template:', error);
        this.toastr.error('Failed to download category template.');
      },
    });
  }

  // Helper to trigger file download
  private triggerDownload(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
    this.toastr.success('File downloaded successfully!');
  }

  // Trigger file input for upload
  triggerUpload(type: 'CREATE' | 'UPDATE' | 'CATEGORY_CREATE'): void {
    this.uploadType = type;
    this.fileInput.nativeElement.value = ''; // Reset input
    this.fileInput.nativeElement.click();
  }

  // Handle file selection
  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      this.toastr.error('Invalid file format. Please upload an Excel file.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    if (this.uploadType === 'CREATE') {
      // Two-step flow: dry-run first, show what will happen (row errors +
      // masters that will be auto-created), then commit on confirmation.
      this.pendingUploadFile = file;
      this.uploadInProgress = true;
      this.productService.bulkUploadPreview(formData).subscribe({
        next: (res) => {
          this.uploadInProgress = false;
          this.uploadPreview = res;
          this.modalService.open(this.bulkUploadPreviewModalRef, {
            size: 'lg',
            centered: true,
            scrollable: true,
          });
        },
        error: (err) => {
          this.uploadInProgress = false;
          this.pendingUploadFile = null;
          this.onUploadError(err);
        },
      });
    } else if (this.uploadType === 'UPDATE') {
      this.productService.bulkUpdateUpload(formData).subscribe({
        next: (res) => this.onUploadSuccess('Bulk update successful!'),
        error: (err) => this.onUploadError(err),
      });
    } else if (this.uploadType === 'CATEGORY_CREATE') {
      this.productService.bulkUploadCategories(formData).subscribe({
        next: (res) =>
          this.onUploadSuccess('Category bulk creation successful!'),
        error: (err) => this.onUploadError(err),
      });
    }
  }

  // The upload is all-or-nothing: any bad row blocks the whole file.
  previewHasErrors(): boolean {
    return !!this.uploadPreview?.errors?.length;
  }

  canCommitUpload(): boolean {
    return (
      !this.uploadInProgress &&
      !this.previewHasErrors() &&
      !!this.uploadPreview?.validRows
    );
  }

  // Download the current error list as an Excel file
  exportUploadErrors(errors?: any[]): void {
    if (!errors?.length) return;
    this.productService.exportBulkUploadErrors(errors).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'product_upload_errors.xlsx';
        link.click();
        window.URL.revokeObjectURL(url);
        this.toastr.success('Error list downloaded.');
      },
      error: () => this.toastr.error('Failed to download the error list.'),
    });
  }

  // Anything the preview says will be auto-created?
  previewWillCreateAnything(): boolean {
    const w = this.uploadPreview?.willCreate;
    if (!w) return false;
    return !!(
      w.departments?.length ||
      w.categories?.length ||
      w.subCategories?.length ||
      w.manufacturers?.length ||
      w.brands?.length ||
      w.vendorManufacturerLinks?.length
    );
  }

  // Commit the previewed product-creation file
  confirmBulkUpload(modal: any): void {
    if (!this.pendingUploadFile) return;
    const formData = new FormData();
    formData.append('file', this.pendingUploadFile);
    this.uploadInProgress = true;

    this.productService.bulkUpload(formData).subscribe({
      next: (res) => {
        this.uploadInProgress = false;
        this.pendingUploadFile = null;
        modal.close();
        this.toastr.success(`Created ${res.created} products successfully!`);
        this.loadProducts();
      },
      error: (err) => {
        // Nothing was created: the server rejects the file as a whole.
        this.uploadInProgress = false;
        modal.close();
        this.onUploadError(err);
      },
    });
  }

  // Handle successful bulk upload
  onUploadSuccess(message: string): void {
    this.toastr.success(message);
    if (this.uploadType === 'CREATE' || this.uploadType === 'UPDATE') {
      this.loadProducts(); // Reload products list
    }
    // For categories, we might not need to reload products immediately, but it's fine.
  }

  // Download Classification Data (Dept/Cat/SubCat)
  downloadClassification(): void {
    this.productService.downloadClassificationData().subscribe({
      next: (blob: Blob) => {
        this.triggerDownload(blob, 'classification_data.xlsx');
      },
      error: (error: any) => {
        console.error('Error downloading classification data:', error);
        this.toastr.error('Failed to download classification data.');
      },
    });
  }

  // Download Product Master Data
  downloadProductMaster(): void {
    this.productService.downloadProductMaster().subscribe({
      next: (blob: Blob) => {
        this.triggerDownload(blob, 'product_master.xlsx');
      },
      error: (error: any) => {
        console.error('Error downloading product master:', error);
        this.toastr.error('Failed to download product master.');
      },
    });
  }

  // Confirmation properties
  downloadConfirmationType: 'CLASSIFICATION' | 'PRODUCT_MASTER' | null = null;
  downloadConfirmationName: string = '';
  @ViewChild('downloadConfirmationModal')
  downloadConfirmationModalRef!: TemplateRef<any>;

  openDownloadConfirmation(
    type: 'CLASSIFICATION' | 'PRODUCT_MASTER',
    name: string,
  ) {
    this.downloadConfirmationType = type;
    this.downloadConfirmationName = name;
    this.modalService.open(this.downloadConfirmationModalRef, {
      ariaLabelledBy: 'downloadConfirmationModalLabel',
      centered: true,
    });
  }

  confirmDownloadAction() {
    if (this.downloadConfirmationType === 'CLASSIFICATION') {
      this.downloadClassification();
    } else if (this.downloadConfirmationType === 'PRODUCT_MASTER') {
      this.downloadProductMaster();
    }
    this.closeModal();
  }

  // Handle bulk upload error
  onUploadError(error: any): void {
    console.error('Bulk upload error:', error);
    this.bulkUploadErrors = [];

    if (error.status === 400 && error.error && error.error.errors) {
      this.bulkUploadErrors = error.error.errors;
      this.modalService.open(this.bulkUploadErrorModalRef, {
        size: 'lg',
        centered: true,
        scrollable: true,
      });
    } else {
      const errorMessage =
        error.error?.message || error.message || 'Failed to upload.';
      this.toastr.error(errorMessage);
    }
  }
}
